import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { Vendor } from '../lib/database.types';

export default function RequestLocation() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [requested, setRequested] = useState<Set<string>>(new Set());
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ga_vendors')
        .select('*')
        .eq('status', 'inactive')
        .order('name');
      setVendors((data as Vendor[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return vendors;
    return vendors.filter((v) =>
      v.name.toLowerCase().includes(ql) ||
      (v.neighborhood ?? '').toLowerCase().includes(ql) ||
      (v.address ?? '').toLowerCase().includes(ql) ||
      (v.category ?? '').toLowerCase().includes(ql),
    );
  }, [vendors, q]);

  async function requestVendor(v: Vendor) {
    if (requested.has(v.id)) return;
    setRequested((prev) => new Set(prev).add(v.id));
    const payload = {
      vendor_id: v.id,
      location_name: v.name,
      location_address: v.address,
      location_neighborhood: v.neighborhood,
      location_image_url: v.hero_image_url,
      requester_user_id: user?.id ?? null,
      requester_email: user?.email ?? null,
    };
    await supabase.from('ga_vendor_requests').insert(payload);
    try {
      await supabase.functions.invoke('ga-notify', {
        body: { kind: 'vendor_requested', vendor_id: v.id },
      });
    } catch { /* non-fatal */ }
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 48, paddingBottom: 24 }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: 20 }}>
            <div>
              <div className="eyebrow" style={{ marginBottom: 16 }}>Request a location · {vendors.length} on our radar</div>
              <h1 className="display" style={{ fontSize: 'clamp(40px, 8vw, 72px)', margin: 0, lineHeight: 0.95 }}>
                Want a spot we haven't <span className="serif-italic" style={{ color: 'var(--wine)' }}>activated yet?</span>
              </h1>
              <p style={{ color: 'var(--ink-soft)', fontSize: 'clamp(15px, 2vw, 17px)', marginTop: 14, maxWidth: 640 }}>
                Tap a card to nudge us. We'll reach out to the owner about joining Goaround.
              </p>
            </div>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search locations…"
              style={{ maxWidth: 320, minWidth: 0, width: '100%' }}
            />
          </div>
        </div>
      </section>

      <section style={{ paddingBottom: 32 }}>
        <div className="container">
          <button onClick={() => setManualOpen(true)} className="btn btn-ghost btn-sm">
            Can't find it? Request by name →
          </button>
          {manualOpen && <ManualRequest onClose={() => setManualOpen(false)} userId={user?.id} userEmail={user?.email} />}
        </div>
      </section>

      <section style={{ paddingBottom: 120 }}>
        <div className="container">
          {loading ? (
            <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
          ) : (
            <div className="r-grid-3">
              {filtered.map((v) => (
                <UnavailableCard key={v.id} v={v} requested={requested.has(v.id)} onRequest={() => requestVendor(v)} />
              ))}
              {filtered.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>
                  No matches. Try the manual request button above.
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

function UnavailableCard({ v, requested, onRequest }: { v: Vendor; requested: boolean; onRequest: () => void }) {
  return (
    <div
      className="bar-card"
      style={{ display: 'block', cursor: requested ? 'default' : 'pointer', opacity: requested ? 0.7 : 1 }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: 16,
          overflow: 'hidden',
          background: v.hero_image_url
            ? `url(${v.hero_image_url}) center/cover no-repeat`
            : `linear-gradient(135deg, oklch(0.72 0.12 ${(v.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((v.name.charCodeAt(0) * 7) + 30) % 360}))`,
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
        <div style={{ position: 'absolute', top: 12, left: 12 }}>
          <span className="mono" style={{ background: 'var(--bg)', color: 'var(--ink)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 10px', borderRadius: 999 }}>Not yet on Goaround</span>
        </div>
      </div>
      <div style={{ paddingTop: 16 }}>
        <div className="row-between">
          <h3 className="display" style={{ fontSize: 22, margin: 0 }}>{v.name}</h3>
          {v.neighborhood && <span className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-soft)', textTransform: 'uppercase' }}>{v.neighborhood}</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 12 }}>
          {v.address}{v.category ? ` · ${v.category}` : ''}
        </div>
        <button
          onClick={onRequest}
          disabled={requested}
          className={requested ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {requested ? '✓ Requested' : 'Request this location →'}
        </button>
      </div>
    </div>
  );
}

function ManualRequest({ onClose, userId }: { onClose: () => void; userId?: string; userEmail?: string | null }) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    // No .select() chain: SELECT on ga_vendor_requests is platform-admin-only,
    // so asking for the returned row would roll back the insert for anon users.
    const { error: insErr } = await supabase
      .from('ga_vendor_requests')
      .insert({
        vendor_id: null,
        location_name: name,
        notes: notes || null,
        requester_user_id: userId ?? null,
        requester_email: email || null,
      });
    if (insErr) {
      setError(insErr.message);
      setSubmitting(false);
      return;
    }
    try {
      await supabase.functions.invoke('ga-notify', {
        body: {
          kind: 'vendor_requested',
          location_name: name,
          notes: notes || null,
          requester_email: email || null,
        },
      });
    } catch { /* non-fatal */ }
    setSubmitting(false);
    setDone(true);
  }

  return (
    <div style={{ marginTop: 24, padding: 24, borderRadius: 18, background: 'var(--card)', maxWidth: 600 }}>
      {done ? (
        <>
          <div className="display" style={{ fontSize: 22, marginBottom: 8 }}>Got it.</div>
          <p className="muted" style={{ margin: 0 }}>We'll look into adding it. Thanks for the tip.</p>
          <button onClick={onClose} className="btn btn-ghost btn-sm" style={{ marginTop: 16 }}>Close</button>
        </>
      ) : (
        <form onSubmit={submit} className="stack">
          <div className="row-between">
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>Manual request</div>
            <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-soft)' }}>×</button>
          </div>
          <label>
            <span className="field-label">Location name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            <span className="field-label">Your email (optional)</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </label>
          <label>
            <span className="field-label">Notes (optional)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Address, why you love it, etc." />
          </label>
          {error && <div className="field-error">{error}</div>}
          <button type="submit" disabled={submitting} className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Sending…' : 'Send request →'}
          </button>
        </form>
      )}
    </div>
  );
}
